// The SHOP panel of the hub (R229).
//
// What it shows: the never-sell rule, the three plans beside each other,
// and what a CHOOSE press got back from the checkout route.
//
// What it reads live:
//   - `GET /api/shop/plans` through `getShopPlans`, in a cell of five
//     minutes. A plan table is configuration, so it does not move often.
//   - `Me`, which the shell holds. The tier comes from the account's keys
//     and never from a plan name.
//   - `POST /api/shop/checkout` through `startCheckout`, on a press.
//
// What is honestly absent today:
//   - **The shop is optional.** A deployment with no Paddle block answers
//     503 with `shop_disabled`. The panel prints that state and says that
//     an operator grants a plan by hand until the block exists. It is not
//     a crash and it is not drawn as one.
//   - **FREE is not a row the server serves.** The route lists the plans a
//     person pays for. The free column is a local constant of what an
//     account with no plan already holds, and the card labels it so.
//   - **This build loads no Paddle overlay script.** A checkout answer is
//     printed — the environment, the price id and the two return links —
//     and no overlay opens. The card says that in as many words.
//
// The panel computes no price. `displayPrice` is a string the server
// formatted, and the client prints it as it arrived.

import { button, tag } from '../../choice/choice';
import {
  ApiError, CODE_REGISTRATION_REQUIRED, CODE_SHOP_DISABLED,
  getShopPlans, messageOf, startCheckout,
  type Checkout, type ShopPlan, type ShopPlans,
} from '../../../net/api';
import { TTL_ON_OPEN, tierOf, type Cell, type Tier } from '../hubstate';
import { hubShop as S, hubSupport as SUP } from '../../../gen/strings';
import {
  PLAN_WORDS, card, col, empty, esc, h1, lede, page, pending, ctlRow, readable, rows, type EmptyPress,
  type Panel, type PanelContext, type Row,
} from '../panel';

/** `CodePlanUnknown`: the id in the request is not a plan this deployment
 *  carries. It comes from the generated gen/balance.ts, the same
 *  data/contracts/api.toml the Go half is written from (R341);
 *  `net/api.ts` re-exports the other three. */
import { CODE_PLAN_UNKNOWN, MAX_QUEUE_HOURS } from '../../../gen/balance';

/**
 * What an account with no plan already has.
 *
 * This is a local constant and not a server row, because `/api/shop/plans`
 * lists what a person may buy. Every line below is a rule and not a price:
 * the core loop is open to everyone (monetization.md "Free tier
 * guarantees"), the queue ceiling is 12 h for every commander (R212), and
 * the daily arcade cap is one run by default (R221, R224).
 */
const FREE_ROWS: Row[] = [
  [S.free_core, S.free_core_value, 'good'],
  [S.free_arcade, S.free_arcade_value],
  [S.free_wardroom, S.free_wardroom_value, 'good'],
  [S.free_summary, S.free_summary_value, 'good'],
  [S.free_queue, S.free_queue_value({ hours: MAX_QUEUE_HOURS })],
];

/** What the panel says about one refusal, by the wire code; every other
 *  code reads `refusal_other`. The server's own sentence goes beside it,
 *  because a person may read it and a client may not parse it. */
const REFUSALS: ReadonlyMap<string, string> = new Map([
  [CODE_REGISTRATION_REQUIRED, S.refusal_registration_required],
  [CODE_PLAN_UNKNOWN, S.refusal_plan_unknown],
  [CODE_SHOP_DISABLED, S.shop_not_connected],
]);

/** The plan table, in one cell. The PLAY panel reads the same cell to
 *  learn whether the shop is closed, so the two panels make one request
 *  (ui-map-interface.md §16.6). */
export function shopPlansCell(ctx: PanelContext): Cell<ShopPlans> {
  return ctx.data.cell<ShopPlans>('shop.plans', TTL_ON_OPEN, getShopPlans);
}

/** True when the route answered `shop_disabled`: this deployment carries
 *  no payment configuration. A cell that still loads, or that failed for
 *  another reason, is not a closed shop. */
export function shopIsDisabled(cell: Cell<ShopPlans>): boolean {
  return cell.value === undefined
    && cell.error instanceof ApiError && cell.error.code === CODE_SHOP_DISABLED;
}

export function createShopPanel(ctx: PanelContext): Panel {
  /** The plan id whose checkout call is in flight. '' is none. */
  let busy = '';
  /** The last checkout answer, which the panel prints instead of opening. */
  let handoff: Checkout | null = null;
  /** The refusal of the last press. Both fields are '' while there is none. */
  let errCode = '';
  let errMessage = '';

  /** One entitlement key, as a person reads it. */
  function keyRow(code: string): Row {
    return [readable(code), S.entitlement_granted, 'good'];
  }

  /** One column of the comparison. `held` gives it the YOUR PLAN line and
   *  takes its CHOOSE control away. */
  function planCard(
    name: string, price: string, held: boolean, list: Row[], note: string,
    action: string,
  ): string {
    const head = held ? `<div class="planhead">${S.plan_held_head}</div>` : '';
    const foot = held
      ? ctlRow(tag(S.plan_held_pill, 'accent'))
      : action === ''
        ? ''
        : ctlRow(button(busy === action ? S.plan_opening : S.plan_choose({ plan: name }),
          { act: 'buy', data: { plan: action } }, { variant: 'primary', disabled: busy === action }));
    return card(name, '¤', price,
      head + rows(list) + (note === '' ? '' : lede(note)) + foot);
  }

  /** The three columns, side by side on a wide pane and stacked on a
   *  narrow one. `split` carries two columns, and this card needs three.
   *  The block spaces itself with the page's own gap and never with a
   *  margin of its own (R231), so the three columns take `--hub-gap` as
   *  well and the whole section keeps one rhythm. */
  function columns(inner: string): string {
    return ctx.wide() ? `<div class="split3">${inner}</div>` : col(inner);
  }

  /** The press of a closed or an empty SHOP: ASK A PERSON of SUPPORT,
   *  named by the page it opens and never by the word SUPPORT, which names
   *  the help section and the Supporter tier (R2189). An account with a
   *  paid plan has nothing to ask for, so it gets no press. */
  function askPress(tier: Tier): EmptyPress | undefined {
    return tier === 'free' ? { label: SUP.sec_ask, tab: 'support', section: 'ask' } : undefined;
  }

  /** The state of a deployment that carries no payment configuration.
   *  Its one press leads to ASK A PERSON and never into the closed shop
   *  (R1734, R1742). An account with a paid plan reads that plan and no
   *  "ask for a plan" line (R2046). */
  function disabled(tier: Tier): string {
    const why = tier === 'free' ? S.shop_disabled_why : S.shop_disabled_why_held({ plan: PLAN_WORDS[tier] });
    return card(S.card_plans_title, '¤', '', empty(S.shop_not_connected, why, askPress(tier)));
  }

  /** The plan comparison, once the route answered. */
  function table(plans: ShopPlan[], tier: Tier): string {
    const free = planCard(PLAN_WORDS.free, S.plan_free_price, tier === 'free',
      FREE_ROWS, S.plan_free_note, '');
    const paid = plans.map((p) => planCard(
      p.name === '' ? p.id.toUpperCase() : p.name,
      p.interval
        ? S.plan_price_interval({ price: p.displayPrice, interval: p.interval.toUpperCase() })
        : p.displayPrice,
      p.tier === tier,
      p.entitlements.map(keyRow),
      p.description ?? '',
      p.id,
    )).join('');
    return columns(free + paid);
  }

  /** What a checkout answer says, printed and not opened. */
  function handoffCard(c: Checkout): string {
    return card(S.card_handoff_title, '◈', c.plan.displayPrice,
      rows([
        [S.handoff_plan, c.plan.name === '' ? c.plan.id : c.plan.name, 'gold'],
        [S.handoff_environment, c.environment === '' ? '—' : c.environment.toUpperCase()],
        [S.handoff_price_id, c.priceId],
        [S.handoff_client_token,
          c.clientToken ? S.handoff_token_sent : S.handoff_token_none,
          c.clientToken ? 'good' : 'bad'],
        [S.handoff_on_success, c.successUrl],
        [S.handoff_on_cancel, c.cancelUrl],
      ])
      + lede(S.handoff_lede)
      + ctlRow(button(S.pill_clear, { act: 'clear' })));
  }

  return {
    key: 'shop',

    open(): void {
      // A refusal the player walked away from is not news when they come
      // back. The plan table itself keeps its cell.
      errCode = '';
      errMessage = '';
    },

    html(): string {
      const tier = tierOf(ctx.me());
      const cell = shopPlansCell(ctx);

      let body: string;
      if (cell.value === undefined) {
        body = shopIsDisabled(cell) ? disabled(tier) : card(S.card_plans_title, '¤', '', pending(cell));
      } else if (cell.value.plans.length === 0) {
        const why = tier === 'free' ? S.plans_empty_why : S.plans_empty_why_held({ plan: PLAN_WORDS[tier] });
        body = card(S.card_plans_title, '¤', '', empty(S.plans_empty, why, askPress(tier)));
      } else {
        body = table(cell.value.plans, tier);
      }

      const err = errCode === ''
        ? ''
        : `<div class="err">${esc(REFUSALS.get(errCode) ?? S.refusal_other)}`
          + (errMessage === '' ? '' : `<br>${esc(errMessage)}`) + '</div>';

      return page(
        h1(S.title, S.subtitle),
        card(S.card_rule_title, '!', '', lede(S.rule)),
        body,
        err,
        handoff === null ? '' : handoffCard(handoff),
      );
    },

    act(action: string, el: HTMLElement): void {
      if (action === 'clear') { handoff = null; ctx.refresh(); return; }
      if (action !== 'buy') return;
      const plan = el.dataset.plan ?? '';
      if (plan === '' || busy !== '') return;
      busy = plan;
      errCode = '';
      errMessage = '';
      handoff = null;
      ctx.refresh();
      startCheckout(plan).then(
        (c) => {
          busy = '';
          handoff = c;
          ctx.play('sfx-menu-purchase');
          ctx.refresh();
        },
        (e: unknown) => {
          busy = '';
          errCode = e instanceof ApiError ? e.code : 'other';
          errMessage = messageOf(e);
          ctx.play('sfx-menu-error');
          ctx.refresh();
        },
      );
    },
  };
}
